import {
  Component,
  computed,
  ContentChild,
  DestroyRef,
  Directive,
  effect,
  ElementRef,
  inject,
  input,
  Input,
  OnInit,
  output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Pagination } from '../../../services/api/api';
import { UtilService } from '../../../services/util.service';
import { QueryParamService } from '../../../services/lists/query-params';
import { Params, Router } from '@angular/router';
import { UserService } from '../../../services/user.service';
import { ModalNavigationComponent } from '../../../services/gui/modal/modal-navigation-component';
import { ViewSetting } from '../../../services/global/global-interfaces';
import { StringTransformationService } from '../../../services/transformation-helper/string-transformation.service';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { SizeService } from '../../../services/size-detection/size.service';
import { animate, style, transition, trigger } from '@angular/animations';

@Directive({
  // tslint:disable-next-line:directive-selector
  selector: '[filter-panel]',
  standalone: false,
})
export class SbFilterPanelWrapperDirective {
  @Input() classes: string;
}

@Directive({
  // tslint:disable-next-line:directive-selector
  selector: '[footer-more]',
  standalone: false,
})
export class SbFooterMoreWrapperDirective {
  @Input() classes: string;
}

@Component({
  selector: 'sb-table-wrapper-new',
  templateUrl: './sb-table-wrapper-new.component.html',
  styleUrls: ['./sb-table-wrapper-new.component.scss'],
  animations: [
    trigger('slideInOutBottom', [
      transition(':enter', [
        style({ transform: 'translateY(100%)', opacity: 0 }),
        animate('300ms 200ms ease-out', style({ transform: 'translateY(0)', opacity: 1 })),
      ]),
      transition(':leave', [animate('150ms ease-in', style({ transform: 'translateY(100%)', opacity: 0 }))]),
    ]),
    trigger('fadeOut', [transition(':leave', [animate('500ms ease-out', style({ opacity: 0 }))])]),
    trigger('fadeIn', [transition(':enter', [style({ opacity: 0 }), animate('600ms ease-in', style({ opacity: 1 }))])]),
  ],
  standalone: false,
})
export class SbTableWrapperNewComponent implements OnInit, ModalNavigationComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly stringTransformation = inject(StringTransformationService);
  private readonly sizeService = inject(SizeService);
  @Input() isInModalContext: boolean = false;
  @Input() isInIntegratedContext: boolean = false;

  @ViewChild('tableHead') tableHeadElement: ElementRef;
  @ViewChild('tableBody') tableBodyElement: ElementRef;
  @ContentChild(SbFooterMoreWrapperDirective, { static: false }) footerMore?: SbFooterMoreWrapperDirective;
  @ContentChild(SbFilterPanelWrapperDirective, { static: false }) filterPanel?: SbFilterPanelWrapperDirective;
  @ContentChild('additionalInputTop') additionalInputTop: TemplateRef<any>;
  @ContentChild('thead') thead: TemplateRef<any>;
  @ContentChild('tSelectionMode') tSelectionMode: TemplateRef<any>;
  @ContentChild('tCardHead') tCardHead: TemplateRef<any>;
  @ContentChild('tbody') tbody: TemplateRef<any>;
  @ContentChild('card') card: TemplateRef<any>;
  @Input() headline: string;
  itemName = input<string | null>(null);
  @Input() headerClasses: string[];
  @Input() isFilterPanelOpen: boolean;
  @Input() saveUserSettings: boolean = true;
  @Input() iterateTableRows: boolean = true;
  @Input() isSplitscreen: boolean = false;
  @Input() hidePagination: boolean = false;
  forceViewSetting = input<ViewSetting | null>(null);
  @Input() startSearch: boolean = false;
  @Input() showViewModeChangeButton: boolean = true;
  paginationInitialized: boolean;
  maxSize: number = 8;
  paginationString: string;
  sizes: number[] = [8, 12, 16, 20, 24, 50, 100];
  boundaryLinks: boolean = true;
  resultCount: number;
  debounceTimer: ReturnType<typeof setTimeout> = null;
  queryParams: Params;
  queryParamsParsed: boolean = false;
  pagination = new BehaviorSubject<Pagination>({ page: 1, count: 8, pages: -1, resultcount: -1 });
  paginationChange = output<BehaviorSubject<Pagination>>();
  screenSize = toSignal(this.sizeService.onResize$().pipe(takeUntilDestroyed(this.destroyRef)));

  viewSettingObjectFromUserSetting = computed(() => {
    const viewSettingObject = this.userService.viewSettingObject();
    const name = this.itemName();
    const itemName = name ? name + 'ViewSetting' : null;
    if (itemName && viewSettingObject[itemName]) {
      return { view: viewSettingObject[itemName], default: false };
    }
    return { view: viewSettingObject.defaultView ?? 'list', default: true };
  });

  currentViewSetting = computed(() => {
    const currentViewSetting = this.viewSettingObjectFromUserSetting();
    const forcedViewSetting = this.forceViewSetting();
    const screenSize = this.screenSize();
    if (forcedViewSetting) {
      return forcedViewSetting;
    }
    if (['xs', 'sm', 'md'].includes(screenSize)) {
      return 'card';
    }
    let viewSetting: ViewSetting;
    if (this.isInModalContext) {
      viewSetting = 'list';
    } else if (this.isInIntegratedContext) {
      viewSetting = 'list';
    } else if (currentViewSetting.view) {
      viewSetting = currentViewSetting.view;
    } else {
      viewSetting = 'list';
    }
    return viewSetting;
  });

  isCardDisplayed = computed(() => {
    if (!this.card) {
      // list without cards
      return false;
    }
    return this.currentViewSetting() === 'card';
  });

  userMaxSize = computed(() => {
    const currentUserSettings = this.userService.currentUserSettings();
    const itemName = this.itemName();
    const maxSizeSetting = itemName ? itemName + 'MaxSize' : null;
    const maxSize = parseInt(currentUserSettings?.[maxSizeSetting], 10);
    return maxSize && !isNaN(maxSize) ? maxSize : 8;
  });

  ghosts = computed(() => {
    return new Array(this.userMaxSize());
  });

  constructor(
    public readonly util: UtilService,
    private readonly queryParamService: QueryParamService,
    private readonly userService: UserService,
    private readonly router: Router
  ) {
    this.initPagination();
    // language change
    effect(() => {
      this.userService.selectedLanguage(); // on which signal should the effect be executed

      this.paginationString = this.stringTransformation.getPaginationString(
        this.page ?? 1,
        this.userMaxSize() ?? 8,
        this.itemsData?.length ?? 0,
        this.resultCount ?? -1,
        this.itemName()
      );
    });

    // max Size effect
    effect(() => {
      const maxSize = this.userMaxSize();
      if (this.router.url.includes('admin')) {
        this.maxSize = 8;
      } else {
        this.maxSize = maxSize;
      }
      this.pagination.value.count = this.maxSize;
    });
  }

  // tslint:disable-next-line:variable-name
  _itemsData: any[] = [];

  get itemsData(): any[] {
    return this._itemsData;
  }

  @Input() set itemsData(val: any[]) {
    this._itemsData = val;
    if (this._itemsData) {
      if (this.pagination) {
        this.paginationInitialized = true;
      }
    }

    this.paginationString = this.stringTransformation.getPaginationString(
      this.page,
      this.maxSize,
      this.itemsData?.length,
      this.resultCount,
      this.itemName()
    );
  }

  loading = input<boolean>(true);

  page: number = 1;
  isMultiSelectEnabled = input<boolean>(false);

  saveMaxSize(): void {
    const itemName = this.itemName();
    if (itemName && this.saveUserSettings) {
      this.userService.setUserSetting(itemName + 'MaxSize', this.maxSize.toString()).then();
    }
  }

  ngOnInit(): void {
    if (!this.queryParamsParsed) {
      this.parseQueryParams().then(() => {
        this.queryParamsParsed = true;
        this.requestDebounced();
      });
    }
  }

  parseQueryParams(): Promise<void> {
    return new Promise(res => {
      if (!this.isInModalContext && !this.isInIntegratedContext) {
        this.parsePageParams();
      }
      res();
    });
  }

  parsePageParams() {
    try {
      const pageStr: string = this.queryParamService.getQueryParam('page');
      this.page = +pageStr;
    } catch (e) {
      console.log('Parsingerror for page variable: ', e);
      this.page = 1;
    }
    if (!this.page || this.page < 1) {
      this.page = 1;
    }
    this.pagination.value.page = this.page;
  }

  initPagination() {
    this.pagination.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((e: Pagination) => {
      if (e.page > e.pages && e.pages > 0) {
        this.pagination.next({ ...e, page: e.pages });
        this.page = e.pages;
        this.requestDebounced();
        return;
      }
      this.maxSize = e.count;
      this.resultCount = e.resultcount;
    });
  }

  requestDebounced() {
    if (this.queryParamsParsed && !this.isInModalContext && !this.isInIntegratedContext) {
      this.refreshQueryParams();
    }
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      // options push
      this.paginationChange.emit(this.pagination);
    }, 500);
  }

  refreshQueryParams() {
    if (this.isInModalContext || this.isInIntegratedContext) {
      return;
    }

    if (this.page) {
      this.queryParamService.setQueryParam('page', '' + this.page);
    } else {
      this.queryParamService.clearQueryParam('page');
    }
  }

  isSplitButtonVisible(): boolean {
    return !this.isInIntegratedContext && !this.isInModalContext && this.showViewModeChangeButton && !!this.card;
  }

  changePagination($event: Pagination) {
    if (this.pagination?.value.count !== $event.count) {
      this.maxSize = $event.count;
      this.saveMaxSize();
    }
    if (this.pagination?.value.page !== $event.page) {
      this.page = $event.page;
    }
    this.pagination.next($event);
    this.requestDebounced();
  }
}
